import { useAuth } from '@clerk/clerk-react';
import { Download, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { useAlarmReport } from '../api/useReports.js';
import { useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

const DAY_MS = 24 * 60 * 60_000;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function dateToWindowIso(dateStr: string, endOfDay: boolean): string {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function formatSeconds(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

export default function AlarmReport() {
  const me = useMe();
  const teams = useTeamsList();
  const allTeams = teams.data?.teams ?? [];
  const today = new Date();
  // Alarms are reviewed monthly by safety managers, so default to 30 days.
  const monthAgo = new Date(today.getTime() - 30 * DAY_MS);
  const [fromDate, setFromDate] = useState(toDateInputValue(monthAgo));
  const [toDate, setToDate] = useState(toDateInputValue(today));
  const [teamId, setTeamId] = useState<string>('');
  const fromIso = useMemo(() => dateToWindowIso(fromDate, false), [fromDate]);
  const toIso = useMemo(() => dateToWindowIso(toDate, true), [toDate]);
  const report = useAlarmReport(
    { from: fromIso, to: toIso, teamIds: teamId || undefined },
    Boolean(me.data),
  );
  const rows = report.data?.rows ?? [];
  const orgTotals = rows.reduce(
    (acc, r) => {
      acc.total += r.total_count;
      acc.realIncident += r.by_outcome.real_incident;
      acc.falseAlarm += r.by_outcome.false_alarm;
      acc.unclassified += r.by_outcome.unclassified;
      return acc;
    },
    { total: 0, realIncident: 0, falseAlarm: 0, unclassified: 0 },
  );

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Alarms
          </h1>
          <p className="text-sm text-ink-faint">
            Per-worker alarms over the window. Outcome is set by the monitor on the resolve form.
            Unclassified covers pre-migration rows + ones a monitor closed without picking.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Picker label="Team" value={teamId} onChange={setTeamId}>
            <option value="">
              {me.data?.capabilities?.monitor || me.data?.capabilities?.admin
                ? 'All teams'
                : 'My teams'}
            </option>
            {allTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Picker>
          <DateField label="From" value={fromDate} onChange={setFromDate} />
          <DateField label="To" value={toDate} onChange={setToDate} />
          <CsvDownloadButton
            path="alarms"
            from={fromIso}
            to={toIso}
            teamIds={teamId || undefined}
          />
        </div>
      </div>

      {report.isError && (
        <Banner kind="error">Failed to load report: {(report.error as Error).message}</Banner>
      )}
      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading…</div>
      )}
      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={ShieldAlert}
          title="No alarms in this window"
          description="No alarms were triggered by workers in scope. Try a wider window or a different team."
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3 text-xs">
            <span className="font-mono uppercase tracking-wider text-ink-faint">
              {orgTotals.total} alarms · {orgTotals.realIncident} real · {orgTotals.falseAlarm}{' '}
              false · {orgTotals.unclassified} unclassified
            </span>
          </div>
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">SOS</Th>
                <Th className="text-right">Overdue</Th>
                <Th className="text-right">Real</Th>
                <Th className="text-right">Welfare OK</Th>
                <Th className="text-right">False</Th>
                <Th className="text-right">Unclass.</Th>
                <Th className="text-right">Avg ACK</Th>
                <Th className="text-right">Avg resolve</Th>
                <Th className="text-right">Active</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-surface-3">
                  <Td>
                    <div className="text-sm font-medium text-ink">{r.display_name}</div>
                    {r.job_title && <div className="text-xs text-ink-faint">{r.job_title}</div>}
                  </Td>
                  <Td className="text-right tabular-nums font-semibold">{r.total_count}</Td>
                  <Td className="text-right tabular-nums">{r.by_type.duress}</Td>
                  <Td className="text-right tabular-nums">{r.by_type.overdue_check_in}</Td>
                  <Td className="text-right tabular-nums text-red-700">
                    {r.by_outcome.real_incident}
                  </Td>
                  <Td className="text-right tabular-nums text-emerald-700">
                    {r.by_outcome.welfare_check_ok}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-mute">
                    {r.by_outcome.false_alarm}
                    {r.total_count > 0 && r.by_outcome.false_alarm > 0 && (
                      <span
                        className={cn(
                          'ml-1 text-[11px] font-semibold',
                          r.by_outcome.false_alarm / r.total_count >= 0.5
                            ? 'text-red-600'
                            : r.by_outcome.false_alarm / r.total_count >= 0.3
                              ? 'text-amber-600'
                              : 'text-ink-mute',
                        )}
                        title="Share of this worker's alarms that were false"
                      >
                        ({Math.round((r.by_outcome.false_alarm / r.total_count) * 100)}%)
                      </span>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums text-amber-700">
                    {r.by_outcome.unclassified}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-dim">
                    {formatSeconds(r.avg_ack_seconds)}
                  </Td>
                  <Td className="text-right tabular-nums text-ink-dim">
                    {formatSeconds(r.avg_resolve_seconds)}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {r.currently_active > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                        {r.currently_active}
                      </span>
                    ) : (
                      <span className="text-ink-mute">0</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      >
        {children}
      </select>
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      />
    </div>
  );
}

function Banner({ kind, children }: { kind: 'error'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-4 rounded-md border px-4 py-3 text-sm',
        kind === 'error' && 'border-red-200 bg-red-50 text-red-800',
      )}
    >
      {children}
    </div>
  );
}

function CsvDownloadButton({
  path,
  from,
  to,
  teamIds,
}: {
  path: string;
  from: string;
  to: string;
  teamIds?: string;
}) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    setBusy(true);
    try {
      const token = await getToken();
      const sp = new URLSearchParams({ from, to });
      if (teamIds) sp.set('team_ids', teamIds);
      const res = await fetch(`${API_BASE_URL}/reports/${path}.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${path}-${from.slice(0, 10)}-to-${to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
